import React from "react";
import { cn } from "@/lib/utils";
import { EnvironmentalData } from "@/services/environmentalDataService";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import DataSourceInfo from "@/components/DataSourceInfo";

interface DeforestationTabProps {
  data: EnvironmentalData;
}

// Closure for custom marker for highest/lowest points
const makeCustomDot = (max: number, min: number) => (props: any) => {
  const { cx, cy, value } = props;
  const isMax = value === max;
  const isMin = value === min;
  if (!isMax && !isMin) return null;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill={isMax ? "#a21caf" : "#2563eb"}
        stroke="#fff"
        strokeWidth={3}
      />
      <foreignObject
        x={cx - 30}
        y={cy - 45}
        width={60}
        height={30}
        className="overflow-visible"
      >
        <div
          className={`
          ${isMax ? "bg-fuchsia-700" : "bg-blue-600"} 
          text-white rounded-2xl px-2.5 py-0.5 font-bold text-sm 
          text-center shadow-md inline-block font-sans
        `}
        >
          {value} km²
        </div>
      </foreignObject>
    </g>
  );
};

// Add a function to get the risk badge for deforestation
const getDeforestationRiskBadge = (rate: number) => {
  if (rate >= 2.0)
    return (
      <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-xs font-medium">
        High
      </span>
    );
  if (rate >= 1.0)
    return (
      <span className="px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-500 text-xs font-medium">
        Moderate
      </span>
    );
  if (rate >= 0.5)
    return (
      <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-xs font-medium">
        Low
      </span>
    );
  return (
    <span className="px-2 py-0.5 rounded-full bg-green-600/20 text-green-600 text-xs font-medium">
      Minimal
    </span>
  );
};

const DeforestationTab: React.FC<DeforestationTabProps> = ({ data }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const tooltipContentStyle = isDark
    ? {
        backgroundColor: "#18181b",
        border: "1px solid #27272a",
        borderRadius: 12,
        color: "#fff",
        fontFamily: "Inter, sans-serif",
        fontWeight: 600,
        fontSize: 14,
      }
    : {
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        color: "#222",
        fontFamily: "Inter, sans-serif",
        fontWeight: 600,
        fontSize: 14,
      };
  const tooltipLabelStyle = isDark
    ? {
        fontWeight: 700,
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
        color: "#fff",
      }
    : {
        fontWeight: 700,
        fontSize: 14,
        fontFamily: "Inter, sans-serif",
        color: "#222",
      };

  const getStatusColor = (rate: number) => {
    if (rate >= 2.0) return "text-red-500 dark:text-red-400";
    if (rate >= 1.0) return "text-orange-500 dark:text-orange-400";
    if (rate >= 0.5) return "text-amber-500 dark:text-amber-400";
    return "text-primary";
  };

  const getStatusBg = (rate: number) => {
    if (rate >= 2.0) return "bg-red-500/10 border-red-500/20";
    if (rate >= 1.0) return "bg-orange-500/10 border-orange-500/20";
    if (rate >= 0.5) return "bg-amber-500/10 border-amber-500/20";
    return "bg-primary/10 border-primary/20";
  };

  const chartData = data.deforestation.yearlyData;
  const values = chartData.map((d) => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const yPadding = Math.max(10, Math.round((max - min) * 0.15));

  return (
    <div className="space-y-6">
      {/* Hero Metrics Card */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-background via-muted/20 to-muted/40 p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16" />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Annual Deforestation Rate
            </div>
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "text-4xl font-bold",
                  getStatusColor(data.deforestation.ratePerYear),
                )}
              >
                {data.deforestation.ratePerYear}%
              </span>
              {getDeforestationRiskBadge(data.deforestation.ratePerYear)}
            </div>
            <p className="text-sm text-left text-muted-foreground">
              Year-over-year forest coverage change
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-destructive" />
              Total Forest Lost
            </div>
            <div className="text-4xl text-left font-bold text-primary">
              {data.deforestation.totalLost.toLocaleString()}{" "}
              <span className="font-normal text-sm">km²</span>
            </div>
            <p className="text-sm text-left text-muted-foreground">
              Square kilometers since monitoring began
            </p>
          </div>
        </div>
      </div>

      {/* Primary Causes Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Primary Causes</h3>
          <span className="text-sm text-muted-foreground">
            {data.deforestation.primaryCauses.length} identified factors
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.deforestation.primaryCauses.map((cause, index) => (
            <div
              key={cause}
              className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-primary/30"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-sm font-bold text-primary">
                    {index + 1}
                  </span>
                </div>
                <span className="font-medium">{cause}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chart Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold">Deforestation Trend Analysis</h3>
          <div className="text-sm text-muted-foreground">
            {chartData.length} years of data
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient id="defor-gradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#40E0D0" />
                  <stop offset="50%" stopColor="#f43f5e" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient id="area-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#40E0D0" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#40E0D0" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke={isDark ? "#374151" : "#e5e7eb"}
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="year"
                stroke={isDark ? "#9ca3af" : "#6b7280"}
                tick={{
                  fontWeight: 500,
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                }}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
              />
              <YAxis
                stroke={isDark ? "#9ca3af" : "#6b7280"}
                tick={{
                  fontWeight: 500,
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                }}
                axisLine={false}
                tickLine={false}
                domain={[min - yPadding, max + yPadding]}
                tickFormatter={(value) => `${value} km²`}
              />
              <Tooltip
                contentStyle={tooltipContentStyle}
                labelStyle={tooltipLabelStyle}
                formatter={(value: any) => [`${value} km²`, "Forest Lost"]}
              />
              <Line
                key="deforestation-line"
                type="monotone"
                dataKey="value"
                stroke="url(#defor-gradient)"
                strokeWidth={3}
                dot={makeCustomDot(max, min)}
                activeDot={{
                  r: 6,
                  stroke: "#40E0D0",
                  strokeWidth: 2,
                  fill: "#40E0D0",
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Source Information */}
      <DataSourceInfo
        dataType="deforestation"
        updatedAt={data.updatedAt}
        dbSources={data.sources}
      />
    </div>
  );
};

export default DeforestationTab;
